import React from "react";
import {
  Activity,
  Wrench,
  TrendingUp,
  Coins,
  Handshake,
  ArrowRight,
  Zap,
  Database,
  Cpu,
  Layers,
  CheckCircle2,
  Target,
} from "lucide-react";
import { PageHeader, SectionCard } from "@/components/Shared";
import RoiCalculator from "@/components/RoiCalculator";
import {
  currentVsPotential,
  valueLevers,
  turnaroundRoadmap,
  techStack,
  valueImpact,
} from "@/data/extraData";

const ICONS = { Activity, Wrench, TrendingUp, Coins, Handshake };

const accentMap = {
  amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", iconBg: "bg-amber-100" },
  blue: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", iconBg: "bg-blue-100" },
  green: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", iconBg: "bg-emerald-100" },
  slate: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", iconBg: "bg-slate-100" },
};

const layerIcons = [Target, Cpu, Database, Layers];

export default function ValueCreation() {
  return (
    <div data-testid="page-value-creation">
      <PageHeader
        eyebrow="Value Creation & Opportunity"
        title="From operational to optimised — the path to a R7.12bn asset."
        subtitle="How a buyer can lift Tzaneen's performance through alignment, predictive technology, and disciplined execution."
      />

      {/* 1. Current vs Potential */}
      <SectionCard
        title="Current State vs Potential State"
        description="Measurable performance deltas between today's run-state and the achievable target state"
        className="mb-6"
        action={<ArrowRight size={16} className="text-amber-700" />}
      >
        <div className="overflow-x-auto" data-testid="current-vs-potential-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-3 pr-4 font-semibold">Dimension</th>
                <th className="py-3 pr-4 font-semibold">Current</th>
                <th className="py-3 pr-4 font-semibold">Potential</th>
                <th className="py-3 pr-4 font-semibold">Business impact</th>
              </tr>
            </thead>
            <tbody>
              {currentVsPotential.map((r) => (
                <tr key={r.metric} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="py-3 pr-4 font-medium text-slate-900">{r.metric}</td>
                  <td className="py-3 pr-4">
                    <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-slate-50 text-slate-700 text-xs font-medium border border-slate-200">
                      {r.current}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-200">
                      <ArrowRight size={12} /> {r.potential}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-slate-700 text-xs leading-relaxed">{r.impact}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* 2. Key Value Levers */}
      <div className="mb-6" data-testid="value-levers-grid">
        <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">Key Value Levers</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {valueLevers.map((l, i) => {
            const Icon = ICONS[l.icon] || Activity;
            const a = accentMap[l.accent] || accentMap.slate;
            return (
              <div
                key={l.title}
                data-testid={`value-lever-${i}`}
                className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col"
              >
                <div className={`h-10 w-10 rounded-md ${a.iconBg} ${a.border} border flex items-center justify-center mb-3`}>
                  <Icon size={18} className={a.text} />
                </div>
                <h3 className="font-display text-base font-semibold text-slate-900 mb-2">{l.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed flex-1">{l.description}</p>
                <div className={`mt-4 pt-4 border-t border-slate-100 text-xs ${a.text} font-semibold`}>
                  {l.benefit}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. Roadmap */}
      <SectionCard
        title="Rapid Turnaround Roadmap"
        description="A phased 12-month value-realisation plan · workforce opportunities link directly into these phases"
        className="mb-6"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-0 relative" data-testid="turnaround-roadmap">
          {turnaroundRoadmap.map((phase, i) => (
            <div key={phase.phase} id={`phase-${i + 1}`} className="relative scroll-mt-8">
              {/* Connector line for desktop */}
              {i < turnaroundRoadmap.length - 1 && (
                <div className="hidden lg:block absolute top-7 left-[calc(50%+24px)] right-[-24px] h-0.5 bg-slate-200" />
              )}

              <div className="flex items-center gap-3 mb-4 relative">
                <div
                  className="h-12 w-12 rounded-full flex items-center justify-center font-display font-bold text-white relative z-10"
                  style={{ background: phase.color }}
                >
                  {i + 1}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: phase.color }}>
                    {phase.phase} · {phase.duration}
                  </div>
                  <div className="font-display text-base font-semibold text-slate-900">{phase.title}</div>
                </div>
              </div>

              <div
                className="bg-white border-2 rounded-lg p-4 mr-0 lg:mr-6"
                style={{ borderColor: phase.color }}
                data-testid={`roadmap-phase-${i}`}
              >
                <ul className="space-y-2">
                  {phase.items.map((it, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" style={{ color: phase.color }} />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* 3.5 ROI Calculator */}
      <div className="mb-6">
        <RoiCalculator />
      </div>

      {/* 4. Technology Enablement */}
      <SectionCard
        title="Technology Enablement Layer"
        description="The integrated stack that powers value creation — from the field to the boardroom"
        className="mb-6"
        action={<Zap size={16} className="text-amber-700" />}
      >
        <div className="space-y-3" data-testid="tech-stack-layers">
          {techStack.map((layer, i) => {
            const Icon = layerIcons[i] || Layers;
            return (
              <div
                key={layer.layer}
                className="bg-gradient-to-r from-slate-50 to-white border border-slate-200 rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-4 hover:shadow-sm transition-shadow"
                data-testid={`tech-layer-${i}`}
              >
                <div className="flex items-center gap-3 md:w-72">
                  <div className="h-10 w-10 rounded-md bg-slate-900 flex items-center justify-center flex-shrink-0">
                    <Icon size={16} className="text-amber-400" />
                  </div>
                  <div>
                    <div className="font-display text-sm font-semibold text-slate-900">{layer.layer}</div>
                    <div className="text-[11px] text-slate-500">{layer.desc}</div>
                  </div>
                </div>
                <div className="flex-1 flex flex-wrap gap-2">
                  {layer.items.map((it) => (
                    <span key={it} className="px-2.5 py-1 rounded-md bg-white border border-slate-200 text-xs text-slate-700 font-medium">
                      {it}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 text-[11px] text-slate-500">
          The MAIP platform you are using is the top "Decision & Workflow" layer — already deployed for this asset.
        </div>
      </SectionCard>

      {/* 5. Value Impact Summary */}
      <div className="bg-slate-900 rounded-lg p-6 md:p-8 text-white grain relative overflow-hidden" data-testid="value-impact-summary">
        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-400 mb-2">
          Value impact summary
        </div>
        <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight max-w-2xl">
          What the buyer captures within 24 months of close.
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-6 border-t border-white/10">
          {valueImpact.map((v) => (
            <div key={v.label}>
              <div className="text-[10px] uppercase tracking-wider text-amber-400 font-semibold">{v.label}</div>
              <div className="font-display text-3xl md:text-4xl font-bold mt-1">{v.value}</div>
              <div className="text-xs text-white/60 mt-1">{v.note}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
